<template>
  <div class="kiosk-layout" :class="{ 'priority-mode': isPriorityMode }">
    <div class="kiosk-header" v-if="showHeader">
      <div class="institution-logo" v-if="showLogo">
        <img src="@/assets/logo.png" alt="Institution Logo" />
      </div>
      <h1 class="kiosk-title">{{ kioskName }}</h1>
    </div>

    <div class="kiosk-body">
      <kiosk-flow-orchestrator 
        v-if="!loading" 
        @set-priority="setPriorityMode" 
        ref="flowOrchestrator"
      />
      <div v-else class="loading-container">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p>Loading...</p>
      </div>
    </div>

    <div class="kiosk-footer">
      <div class="kiosk-navigation">
        <button 
          v-if="canGoBack" 
          class="btn btn-secondary back-btn"
          @click="goBack"
        >
          <i class="fas fa-arrow-left"></i> Back
        </button>
        
        <button 
          v-if="!isFirstStep" 
          class="btn btn-danger exit-btn"
          @click="resetFlow"
        >
          <i class="fas fa-home"></i> Exit
        </button>
      </div>

      <div class="kiosk-timestamp">
        {{ currentDateTime }}
      </div>
    </div>
  </div>
</template>

<script>
import KioskFlowOrchestrator from '@/components/KioskFlowOrchestrator.vue';
import { settingsApi } from '@/services/api';

export default {
  name: 'KioskLayout',
  components: {
    KioskFlowOrchestrator
  },
  data() {
    return {
      loading: true,
      kioskName: 'HCET Queue Kiosk',
      showLogo: true,
      showHeader: false, // Set to false by default, can be enabled via settings
      isPriorityMode: false,
      currentDateTime: '',
      clockInterval: null,
      touchTimeout: null,
      originalViewportMeta: null,
    };
  },
  computed: {
    isFirstStep() {
      if (this.$refs.flowOrchestrator) {
        return this.$refs.flowOrchestrator.currentStepIndex === 0;
      }
      return true;
    },
    canGoBack() {
      if (this.$refs.flowOrchestrator) {
        return this.$refs.flowOrchestrator.flowHistory.length > 0;
      }
      return false;
    }
  },
  created() {
    this.loadSettings();
    this.updateDateTime();
    this.clockInterval = setInterval(this.updateDateTime, 1000);
    this.enableKioskRestrictions();
  },
  beforeUnmount() {
    if (this.clockInterval) {
      clearInterval(this.clockInterval);
    }
    this.disableKioskRestrictions();
  },
  methods: {
    // Kiosk Restriction Methods
    enableKioskRestrictions() {
      document.addEventListener('contextmenu', this.handleContextMenu);
      document.addEventListener('keydown', this.handleKeyDown);
      document.addEventListener('touchstart', this.handleTouchStart);
      document.addEventListener('touchend', this.handleTouchEnd);
      document.addEventListener('touchcancel', this.handleTouchEnd);
      this.disableZoom();
    },
    disableKioskRestrictions() {
      document.removeEventListener('contextmenu', this.handleContextMenu);
      document.removeEventListener('keydown', this.handleKeyDown);
      document.removeEventListener('touchstart', this.handleTouchStart);
      document.removeEventListener('touchend', this.handleTouchEnd);
      document.removeEventListener('touchcancel', this.handleTouchEnd);
      this.enableZoom();
    },
    handleContextMenu(event) {
      event.preventDefault();
      event.stopPropagation();
      console.warn('Context menu blocked in kiosk mode');
      return false;
    },
    handleKeyDown(event) {
      if (event.key === 'F12' || (event.ctrlKey && event.shiftKey && (event.key === 'I' || event.key === 'J' || event.key === 'C'))) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    },
    handleTouchStart(event) {
      if (this.touchTimeout) {
        clearTimeout(this.touchTimeout);
      }
      this.touchTimeout = setTimeout(() => {
        event.preventDefault();
        event.stopPropagation();
        console.warn('Touch hold blocked in kiosk mode');
      }, 500);
    },
    handleTouchEnd() {
      if (this.touchTimeout) {
        clearTimeout(this.touchTimeout);
        this.touchTimeout = null;
      }
    },
    disableZoom() {
      const viewportMeta = document.querySelector('meta[name="viewport"]');
      if (viewportMeta) {
        this.originalViewportMeta = viewportMeta.getAttribute('content');
      }
      this.setViewportMeta('width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
      document.addEventListener('gesturestart', this.handleGesture);
      document.addEventListener('wheel', this.handleWheel, { passive: false });
    },
    enableZoom() {
      if (this.originalViewportMeta) {
        this.setViewportMeta(this.originalViewportMeta);
      }
      document.removeEventListener('gesturestart', this.handleGesture);
      document.removeEventListener('wheel', this.handleWheel);
    },
    setViewportMeta(content) {
      let viewportMeta = document.querySelector('meta[name="viewport"]');
      if (!viewportMeta) {
        viewportMeta = document.createElement('meta');
        viewportMeta.setAttribute('name', 'viewport');
        document.head.appendChild(viewportMeta);
      }
      viewportMeta.setAttribute('content', content);
    },
    handleGesture(event) {
      event.preventDefault();
      console.warn('Zoom action blocked in kiosk mode');
      return false;
    },
    handleWheel(event) {
      if (event.ctrlKey) {
        event.preventDefault();
        console.warn('Zoom action blocked in kiosk mode');
        return false;
      }
    },

    // Original Methods
    async loadSettings() {
      try {
        const response = await settingsApi.getKioskSettings();
        if (response && response.data) {
          const settings = response.data;
          this.kioskName = settings.kiosk_name || this.kioskName;
          if (settings.theme) {
            const theme = typeof settings.theme === 'string' ? JSON.parse(settings.theme) : settings.theme;
            this.showLogo = theme.show_institution_logo;
          }
        }
      } catch (error) {
        console.error('Failed to load kiosk settings:', error);
      } finally {
        this.loading = false;
      }
    },
    updateDateTime() {
      const now = new Date();
      this.currentDateTime = now.toLocaleString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    },
    goBack() {
      if (this.$refs.flowOrchestrator) {
        this.$refs.flowOrchestrator.handleStepBack();
      }
    },
    resetFlow() {
      if (this.$refs.flowOrchestrator) {
        this.$refs.flowOrchestrator.currentStepIndex = 0;
        this.$refs.flowOrchestrator.flowHistory = [];
        this.$refs.flowOrchestrator.transactionData = {};
      }
    },
    setPriorityMode(isPriority) {
      this.isPriorityMode = isPriority;
    }
  }
};
</script>

<style scoped>
.kiosk-layout {
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
  background-color: var(--brand-surface);
  font-family: var(--font-body);
  background: linear-gradient(180deg, var(--brand-surface) 0%, #ffffff 100%);
}

.kiosk-layout.priority-mode {
  background-color: #fff7dd;
  background: linear-gradient(180deg, #fff7dd 0%, #ffffff 100%);
}

.kiosk-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem;
  background: linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-cyan) 100%);
  color: white;
  text-align: center;
  box-shadow: 0 2px 8px rgba(50, 49, 47, 0.18);
}

.institution-logo img {
  height: 60px;
  margin-bottom: 0.5rem;
}

.kiosk-title {
  margin: 0;
  font-size: 1.8rem;
  font-weight: 600;
}

.kiosk-body {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
}

.loading-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.kiosk-footer {
  padding: 1rem;
  background-color: var(--brand-surface);
  border-top: 1px solid var(--brand-border);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.kiosk-navigation {
  display: flex;
  gap: 1rem;
}

.back-btn, .exit-btn {
  font-size: 1.2rem;
  padding: 0.5rem 1rem;
  border-radius: 12px;
}

.back-btn {
  background-color: var(--brand-dark);
  border-color: var(--brand-dark);
  color: #fff;
}

.back-btn:hover {
  background-color: var(--brand-primary);
  border-color: var(--brand-primary);
  color: #fff;
}

.exit-btn {
  background-color: #dc3545;
  border-color: #dc3545;
}

.kiosk-timestamp {
  font-size: 0.9rem;
  color: var(--secondary-text);
}

/* Portrait mode styling */
@media screen and (orientation: portrait) {
  .kiosk-body {
    padding: 1.5rem;
    justify-content: center;
  }

  .kiosk-title {
    font-size: 2rem;
  }

  .back-btn, .exit-btn {
    font-size: 1.4rem;
    padding: 0.75rem 1.5rem;
  }
}
</style>